import { env } from '@/utils/env';

/** 检查 UserWay 是否启用 */
function isUserWayEnabled(): boolean {
  return env.NEXT_PUBLIC_ENABLE_USERWAY === 'true';
}

/** 与 open/close 共用的 rAF 轮询 id，避免关面板后仍被「打开」轮询再次顶开 */
let userWayScheduleRafId: number | null = null;

function cancelUserWayScheduleRaf() {
  if (typeof window === 'undefined') return;
  if (userWayScheduleRafId != null) {
    cancelAnimationFrame(userWayScheduleRafId);
    userWayScheduleRafId = null;
  }
}

// UserWay widget 加载超时配置
const WIDGET_OPEN_TIMEOUT_FRAMES = 180; // ~3 seconds at 60fps
const WIDGET_CLOSE_TIMEOUT_FRAMES = 45; // ~0.75 seconds at 60fps

/**
 * 尽快打开 UserWay 面板（官方 widgetOpen）。
 * 该函数会尝试立即打开面板，如果 UserWay 尚未加载完成，
 * 会使用轮询机制在最多 180 帧（约 3 秒）内重试。
 *
 * 注意事项：
 * - 仅当 NEXT_PUBLIC_ENABLE_USERWAY=true 时执行
 * - SSR 环境下不会执行
 * - 如果面板已经在打开过程中，会取消之前的轮询避免重复触发
 *
 * @throws 不会抛出异常，失败时会静默重试
 *
 * @see {@link closeUserWayWidget} 关闭面板
 * @see {@link https://userway.org/docs/} UserWay 官方文档
 */
export function openUserWayWidget(): void {
  if (!isUserWayEnabled() || typeof window === 'undefined') return;

  const tryOnce = (): boolean => {
    if (window.UserWay?.widgetOpen) {
      try {
        window.UserWay.widgetOpen();
        return true;
      } catch (err) {
        return false;
      }
    }
    return false;
  };

  if (tryOnce()) return;

  cancelUserWayScheduleRaf();

  queueMicrotask(() => {
    tryOnce();
  });
  window.setTimeout(() => {
    tryOnce();
  }, 0);

  let frames = 0;
  const step = () => {
    if (tryOnce()) {
      userWayScheduleRafId = null;
      return;
    }
    frames += 1;
    if (frames >= WIDGET_OPEN_TIMEOUT_FRAMES) {
      userWayScheduleRafId = null;
      return;
    }
    userWayScheduleRafId = requestAnimationFrame(step);
  };
  userWayScheduleRafId = requestAnimationFrame(step);
}

/**
 * 尽快收起 UserWay 面板（官方 widgetClose），并取消未完成的打开轮询。
 *
 * @see {@link openUserWayWidget} 打开面板
 */
export function closeUserWayWidget(): void {
  if (!isUserWayEnabled() || typeof window === 'undefined') return;

  cancelUserWayScheduleRaf();

  const tryOnce = (): boolean => {
    if (window.UserWay?.widgetClose) {
      try {
        window.UserWay.widgetClose();
        return true;
      } catch (err) {
        return false;
      }
    }
    return false;
  };

  if (tryOnce()) return;

  queueMicrotask(() => {
    tryOnce();
  });
  window.setTimeout(() => {
    tryOnce();
  }, 0);

  let frames = 0;
  const step = () => {
    if (tryOnce()) {
      userWayScheduleRafId = null;
      return;
    }
    frames += 1;
    if (frames >= WIDGET_CLOSE_TIMEOUT_FRAMES) {
      userWayScheduleRafId = null;
      return;
    }
    userWayScheduleRafId = requestAnimationFrame(step);
  };
  userWayScheduleRafId = requestAnimationFrame(step);
}

let userWayHideInitListenerAttached = false;

/**
 * 隐藏 UserWay 默认浮标（与 header / SiteConfig 自定义入口配合）。
 * 可多次调用：仅注册一次 init 监听，并尝试立即 iconVisibilityOff。
 * 建议在根布局由 `UserWayDefaultIconInit` 调用一次；业务侧仅使用 open/close 即可。
 */
export function ensureUserWayDefaultIconHidden(): void {
  if (!isUserWayEnabled() || typeof document === 'undefined') return;

  const hideFromEvent = (ev: Event) => {
    try {
      const inst = (ev as CustomEvent<{ userWayInstance?: { iconVisibilityOff?: () => void } }>)
        .detail?.userWayInstance;
      inst?.iconVisibilityOff?.();
      window.UserWay?.iconVisibilityOff?.();
    } catch (err) {
      // 静默失败
    }
  };

  if (!userWayHideInitListenerAttached) {
    userWayHideInitListenerAttached = true;
    document.addEventListener('userway:init_completed', hideFromEvent, { once: true });
  }

  queueMicrotask(() => {
    try {
      window.UserWay?.iconVisibilityOff?.();
    } catch (err) {
      // 静默失败
    }
  });
  window.setTimeout(() => {
    try {
      window.UserWay?.iconVisibilityOff?.();
    } catch (err) {
      // 静默失败
    }
  }, 0);
}
